import React, { useState } from 'react'
import { Alert, Button, Card, Input } from '@rallyshift/design-system'
import api from '../api/client'
import {
  ROOT_DOMAIN,
  isPlausibleSlug,
  setOrgSlugOverride,
  tenantUrl,
} from '../api/tenant'

interface OrgPickerPageProps {
  /** The slug the host named, when it named one that does not exist. */
  attempted?: string | null
}

/**
 * Where someone lands when the address does not name an organisation: app.rallyshift.io,
 * a typo in the subdomain, or a bookmark from before an organisation moved.
 *
 * The portal is per-organisation all the way down — branding, roster, shifts — so there
 * is no sensible tenant-less version of it to show. Rather than a dead end, this asks
 * for the address and forwards.
 */
const OrgPickerPage: React.FC<OrgPickerPageProps> = ({ attempted }) => {
  const [slug, setSlug] = useState('')
  const [error, setError] = useState('')
  const [checking, setChecking] = useState(false)

  const go = async (e: React.FormEvent) => {
    e.preventDefault()
    const candidate = slug.trim().toLowerCase()

    if (!isPlausibleSlug(candidate)) {
      setError('Vul het webadres van je organisatie in, bijvoorbeeld "winterevent".')
      return
    }

    setChecking(true)
    setError('')
    try {
      // Bouncing someone to a subdomain that does not exist would only strand them
      // again, with less to go on the second time.
      const { available } = await api.organizations.slugAvailable(candidate)
      if (available) {
        setError(`Er is geen organisatie met het adres "${candidate}".`)
        return
      }

      // A stale override would otherwise outrank the host we are about to send them to.
      setOrgSlugOverride(null)
      window.location.assign(tenantUrl(candidate))
    } catch {
      setError('Het adres kon niet gecontroleerd worden. Probeer het opnieuw.')
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="min-h-screen bg-surface-page">
      <header className="flex items-center justify-center bg-action-primary px-4 py-8">
        <span className="font-display text-display-md text-white">RallyShift</span>
      </header>

      <main className="mx-auto w-full max-w-md px-4 py-8">
        <h1 className="font-display text-display-md text-text-primary">Ga naar je organisatie</h1>
        <p className="mt-2 text-md text-text-secondary">
          Elke organisatie heeft een eigen adres. Vul hieronder in welke van jou is.
        </p>

        {attempted && (
          <div className="mt-6">
            <Alert variant="danger">
              Er is geen organisatie met het adres &quot;{attempted}&quot;. Controleer of het
              webadres klopt.
            </Alert>
          </div>
        )}

        <div className="mt-6">
          <Card>
            <form onSubmit={go} className="space-y-4">
              <Input
                label="Webadres van je organisatie"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="winterevent"
                hint={`${slug.trim().toLowerCase() || 'jouw-organisatie'}.${ROOT_DOMAIN}`}
                autoFocus
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />

              {error && <Alert variant="danger">{error}</Alert>}

              <Button type="submit" disabled={checking} className="w-full">
                {checking ? 'Bezig…' : 'Doorgaan'}
              </Button>
            </form>
          </Card>
        </div>

        <p className="mt-6 text-center text-md text-text-secondary">
          Nog geen organisatie?{' '}
          <a
            className="font-semibold text-action-primary underline"
            href={`https://${ROOT_DOMAIN}`}
          >
            Lees hoe je begint
          </a>
        </p>
      </main>
    </div>
  )
}

export default OrgPickerPage
